/**
 * Synthetic SMS-signup email helper (#2930).
 *
 * A phone-only signup mints a synthetic `{phone}@sms.oro.fit` address that has
 * no mailbox behind it (backend `auth.ts`, commented "never used for sending").
 * This is the web/native mirror of the backend predicate `isSyntheticSmsEmail`
 * (`backend/src/utils/crypto.ts`) so both surfaces can tell a real, deliverable
 * email from the synthetic placeholder — and route a phone-only user to the
 * add-email recovery flow (this issue) instead of the #1335 change-email flow,
 * whose step 1 sends a proof link to that dead inbox.
 *
 * Shared source: this file lives in `frontend/src/lib/` and is consumed on
 * native through the `@shared/lib` symlink, so the two surfaces cannot drift.
 *
 * Boundary-exact suffix match, NOT a bare `.includes()`: `user@sms.oro.fit.evil.com`
 * ends in `.evil.com`, not `@sms.oro.fit`, and must read as a real address
 * (mirrors the backend helper's lookalike-domain guard).
 */
import { getUserFacingError } from './errorMessages';

export const SYNTHETIC_SMS_EMAIL_DOMAIN = '@sms.oro.fit';

export function isSyntheticSmsEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return email.toLowerCase().endsWith(SYNTHETIC_SMS_EMAIL_DOMAIN);
}

/**
 * Deliberately permissive client-side email shape gate for the add-email flow
 * (#2930). Just enough to reject an empty or obviously-malformed entry before a
 * network round-trip; the backend Zod `.email()` remains the source of truth.
 *
 * NOT a synthetic-address filter: a `foo@sms.oro.fit` candidate PASSES this on
 * purpose so the server's own synthetic-candidate rejection stays reachable
 * (and testable) rather than masked here. Shared so web + native gate
 * identically.
 */
export function isLikelyEmailShape(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/**
 * Error codes whose SERVER message is surfaced verbatim on the add-email
 * recovery surface, instead of the canonical map's copy (#2930, #2990).
 *
 * The canonical map (`getUserFacingError`) flattens `CONFLICT` to "This action
 * conflicts with existing data" and `RATE_LIMITED` to a generic "wait a moment"
 * — which, for a STOP-locked user trying to recover, hides exactly the fact
 * they need: "This email address is already in use." vs "Too many verification
 * attempts." Those two branches are the ones the backend distinguishes on
 * purpose (`backend/src/routes/profile.ts` — `errors.conflict` at the
 * uniqueness check, `errors.rateLimited` at both fixed windows), so they are
 * the two that pass through.
 *
 * Everything else keeps the canonical copy on purpose: `NOT_FOUND` /
 * `INTERNAL_ERROR` / `UNAUTHORIZED` carry server-internal phrasing that helps
 * nobody. `VALIDATION_ERROR` needs no entry — the canonical map already returns
 * the backend message for it verbatim (`'VALIDATION_ERROR': defaultMessage`).
 *
 * THIS SET IS THE ONLY DEFINITION. It lives here rather than in either client
 * because both consume it through `@shared/lib/email`: web resolves inline
 * error copy with `addEmailErrorMessage` below, native replaces the wrapper's
 * toast in place with the same set (`mobile/src/hooks/useProfile.ts`). A
 * per-client copy plus a "keep in sync" comment is not a mechanism — it is a
 * promise that a future edit to one file will silently break.
 */
export const ADD_EMAIL_SERVER_MESSAGE_CODES: ReadonlySet<string> = new Set([
  'CONFLICT',
  'RATE_LIMITED',
]);

/** Copy for a failure this surface has no specific, actionable wording for. */
export const ADD_EMAIL_GENERIC_ERROR = 'Something went wrong. Please try again.';

/**
 * Resolve the user-facing copy for an add-email recovery failure (#2990).
 *
 * Mirrors what the two mutation wrappers already do for toasts, so the web
 * INLINE error and the native TOAST land on identical text for identical
 * responses: extract the first `field_errors` entry on `VALIDATION_ERROR`
 * (#1369 parity — "Enter a valid email address" beats "Validation failed"),
 * then pass the allowlisted codes through and route the rest via the canonical
 * map.
 *
 * Takes plain fields rather than an `ApiError` instance: web and native
 * construct that class from different transport modules (`apiTransport.ts` vs
 * `apiTransport.native.ts`), so an `instanceof` check inside a shared helper
 * would be true on one surface and false on the other. Each caller does its own
 * `instanceof` and hands the fields in.
 */
export function addEmailErrorMessage(error: {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}): string {
  let message = error.message || 'Something went wrong';

  if (error.code === 'VALIDATION_ERROR') {
    const fieldErrors = (error.details as { field_errors?: Array<{ message?: string }> } | undefined)
      ?.field_errors;
    if (fieldErrors?.length && fieldErrors[0]?.message) {
      message = fieldErrors[0].message;
    }
  }

  if (error.code === 'VALIDATION_ERROR' || ADD_EMAIL_SERVER_MESSAGE_CODES.has(error.code)) {
    return message;
  }

  // Ceiling, not a bypass (George round 3, Low #2). Passing `message` as the
  // fallback would make an UNMAPPED code echo the server string — which is
  // `getUserFacingError`'s platform-wide default and fine everywhere else, but
  // this surface has already decided operational strings do not belong in
  // Settings. A future 500-class code the canonical map has never heard of
  // (`DEPENDENCY_ERROR` carrying a Resend or KV sentence) would otherwise land
  // inline in front of the one user who can do nothing with it. Mapped codes
  // still get their canonical copy; only the unmapped tail changes.
  return getUserFacingError(error.code, ADD_EMAIL_GENERIC_ERROR);
}
